// src/components/NetworkStatusBanner/NetworkStatusBanner.tsx
// Bannière pour afficher le statut réseau à l'utilisateur (P2 : indication cache / synchro)

import React, { useState, useEffect, useRef } from 'react';
import { Alert, Button, message } from 'antd';
import { DisconnectOutlined, ThunderboltOutlined, SyncOutlined } from '@ant-design/icons';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { NETWORK_ERROR_EVENT, REFETCH_QUERIES_EVENT } from '../../config/reactQuery';
import { useAppTranslation } from '../../hooks/useAppTranslation';

const SYNC_DURATION_MS = 5000;

const NetworkStatusBanner: React.FC = () => {
  const { t } = useAppTranslation();
  const { isOnline, isSlowConnection, connectionType } = useNetworkStatus();
  const [showSyncing, setShowSyncing] = useState(false);
  const wasOfflineRef = useRef(!navigator.onLine);

  useEffect(() => {
    if (isOnline && wasOfflineRef.current) {
      wasOfflineRef.current = false;
      setShowSyncing(true);
      const timer = setTimeout(() => setShowSyncing(false), SYNC_DURATION_MS);
      return () => clearTimeout(timer);
    }
    if (!isOnline) {
      wasOfflineRef.current = true;
    }
    return undefined;
  }, [isOnline]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      message.warning(detail?.message ?? t('network.errorEvent'));
    };
    window.addEventListener(NETWORK_ERROR_EVENT, handler);
    return () => window.removeEventListener(NETWORK_ERROR_EVENT, handler);
  }, [t]);

  const bannerStyle = {
    position: 'fixed' as const,
    bottom: 16,
    right: 16,
    left: 'auto' as const,
    width: 'auto' as const,
    maxWidth: 360,
    zIndex: 1000,
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  };

  const retryAction = (
    <Button size="small" onClick={() => window.dispatchEvent(new Event(REFETCH_QUERIES_EVENT))}>
      {t('actions.retry')}
    </Button>
  );

  if (showSyncing) {
    return (
      <Alert
        message={t('network.syncingTitle')}
        description={t('network.syncingDescription')}
        type="info"
        icon={<SyncOutlined spin />}
        showIcon
        closable
        action={retryAction}
        style={bannerStyle}
      />
    );
  }

  if (isOnline && !isSlowConnection) {
    return null;
  }

  if (!isOnline) {
    return (
      <Alert
        message={t('network.offlineTitle')}
        description={t('network.offlineDescription')}
        type="warning"
        icon={<DisconnectOutlined />}
        showIcon
        closable
        action={retryAction}
        style={bannerStyle}
      />
    );
  }

  if (isSlowConnection) {
    const connectionLabels: Record<string, string> = {
      'slow-2g': t('network.slow2g'),
      '2g': t('network.slow2gShort'),
      '3g': t('network.slow3g'),
    };

    return (
      <Alert
        message={connectionLabels[connectionType || 'unknown'] || t('network.slowTitle')}
        description={t('network.slowDescription')}
        type="info"
        icon={<ThunderboltOutlined />}
        showIcon
        closable
        action={retryAction}
        style={bannerStyle}
      />
    );
  }

  return null;
};

export default NetworkStatusBanner;
